import React from 'react';

import {
    Group, Space, Stack,
} from '@mantine/core';

import DataTable from './DataTable';
import { TABLE_FILTERS } from './DataTable.constants.ts';
import DataTableCreate from './DataTableCreate';
import DataTableFilters from './DataTableFilters';
import DataTablePaging from './DataTablePaging';
import DataTableSearch from './DataTableSearch';
import useDataFetch from './hooks/useDataFetch';
import useDataOrdering from './hooks/useDataOrdering';
import useStorySchema from './schemas/useStorySchema';
import { PUBLIC_DOMAIN } from '../../const.jsx';
import { getStoryList, removeStory } from '../../utils/neurons';

export default function StoryDataTable({
    type, tag, project,
}) {
    const id = `list-${type}`;

    const { ordering, setOrdering } = useDataOrdering();

    const {
        data,
        mutatation,
        params,
        setParams,
        refreshData,
        ...restProps
    } = useDataFetch({
        id,
        fetchFn: getStoryList,
        fetchProps: {
            storyType: type, project, tag, ordering,
        },
    });

    const { page, limit, search } = params;

    function scrollTop() {
        const scrollContainer = document.getElementsByClassName('main-wrapper')[0];
        scrollContainer.scrollTop = 0;
    }

    function handleSearch(e) {
        const { value } = e.currentTarget;

        setParams({
            search: value,
        });
    }

    function clearSearch() {
        setParams({
            search: '',
        });
    }

    function handleItemDelete(itemId) {
        mutatation.mutate({
            mutationFn: removeStory,
            mutationProps: { id: itemId },
        });
    }

    function handlePageChange(newPage) {
        setParams({
            page: newPage,
        });

        scrollTop();
    }

    function handleLimitChange(newLimit) {
        setParams({
            limit: +newLimit,
        });
    }

    function handleFilterChange(event) {
        const { checked, value, dataset } = event.currentTarget;
        const param = dataset.type;

        if (!checked) {
            setParams({ [param]: '' });

            return;
        }

        setParams({ [param]: value });
    }

    const schema = useStorySchema({
        type,
        project,
        withProject: type === 'news',
        onDelete: handleItemDelete,
    });

    const currentISODate = new Date().toISOString().split('T')[0];
    const path = type === 'news' ? `/news/${currentISODate}/new/` : `/articles/${type}/new/`;

    return (
        <>
            <Stack spacing="xs">
                <DataTableCreate
                    newStoryLink={`${PUBLIC_DOMAIN}${path}`}
                    onRefresh={refreshData}
                />
                <Group>
                    <DataTableSearch
                        search={search}
                        onSearch={handleSearch}
                        onSearchClear={clearSearch}
                    />
                    <DataTableFilters
                        currentParams={params}
                        filters={TABLE_FILTERS[type]}
                        onChange={handleFilterChange}
                    />
                </Group>
            </Stack>
            <Space h="lg" />
            <Space h="4px" />
            <DataTable
                {...restProps}
                ordering={ordering}
                onOrdering={setOrdering}
                schema={schema}
                data={data}
            />
            <Space h="md" />
            <DataTablePaging
                page={page}
                total={data?.total_pages}
                limit={limit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />
        </>
    );
}
